import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Container, Row, Col, Accordion, Card, Button, Form } from 'react-bootstrap';
import { gather_source, ReportPlot } from './Javascript/GenerateReport.js';
import * as RO from './Javascript/ReportObjects.js';
import * as IN from './Javascript/IncidentsReport.js';
import * as G from './Javascript/General.js';
import RouteIncidents from './RouteIncidents';
import MapComponent from './MapComponent';
import './CSS/Bootstrap/bootstrap.css';
import Plot from 'react-plotly.js';
import './CSS/style.css';

interface Props {
  map_ref: any;
}

const IncidentsReport: React.FC<Props> = ({ map_ref }) => {
    const [is_loading, set_is_loading] = useState(true);
    const [are_ids_valid, set_are_ids_valid] = useState(true);
    const [are_classes_valid, set_are_classes_valid] = useState(true);
    const [incidentUuids, setIncidentUuids] = useState<string[]>([]);
    const [incidentsParameters, setIncidentsParameters] = useState<RO.ReportPageParameters | null>(null);
    const [incidents, setIncidents] = useState<RO.RouteIncident[] | null>(null);
    const [incidentPlotData, setIncidentPlotData] = useState<RO.AllZonePlots[] | null>(null);
    const [incidentMarkers, setIncidentMarkers] = useState<RO.Location[] | null>(null);
    const [incidentTitle, setIncidentTitle] = useState<String>("");
    const [areaText, setAreaText] = useState("Map bounds");
    const [mapKey, setMapKey] = useState(Date.now());

    const call_generate_pdf = async ( dl_type: string) => {

          let title_cont = document.querySelectorAll(".incident_total_cont");

          title_cont.forEach(function(cont) {
              let titlesToRemove = cont.querySelectorAll(".sub_title");
              titlesToRemove.forEach(function(title) {
                  title.remove();
              });
          });

          if (are_ids_valid && are_classes_valid) {
              gather_source([], ["plots_container"], "page_break", map_ref);
              if(dl_type == 'pdf') gather_source([], ["plots_container"], "download_pdf", map_ref);
              else if(dl_type == 'word') gather_source([], ["plots_container"], "download_word", map_ref);
              else if(dl_type == 'excel') gather_source([], ["plots_container"], "download_excel", map_ref);
          }
      };

    useEffect(() => {
        if(!is_loading){
            var download_btn = document.getElementById("download_pdf_btn") as HTMLButtonElement;
            if(download_btn){
                download_btn.disabled = false;
                download_btn.addEventListener('click', () => call_generate_pdf('pdf'));
            }
        }
    }, [is_loading]);

    const reloadMap = () => {
        window.location.reload();
    };

    useEffect(() => {
        const fetchDetails = async () => {
            try{
                if(RO.template_uuid && incidentsParameters == null) {
                    const res = await G.getReportParameters(RO.template_uuid);
                    setIncidentTitle("Security Incidents From "+res.multi_incident_date_from+" To "+res.multi_incident_date_to);
                    if(res != null) setIncidentsParameters(res);
                }
                if(RO.current_user && incidentUuids.length === 0) {
                    const res = await IN.getIncidentUuids(RO.current_user);
                    if(res != null) setIncidentUuids(res);
                }
                if(incidentUuids.length > 0 && incidents == null) {
                    const res = await IN.getIncidents(incidentUuids);
                    if(res != null) setIncidents(res);
                }
                if(incidents != null && incidentPlotData == null) setIncidentPlotData(IN.generateIncidentCharts(incidents));
                if(incidents != null && incidentMarkers == null) {
                    incidents.forEach((incident) => {
                        incident.incident_coordinates.id = incident.row.toString() || null;
                     });
                    if(incidentsParameters?.multi_incident_areas?.trim() != "") setAreaText("Map bounds + "+ incidentsParameters?.multi_incident_areas?.trim());

                    setIncidentMarkers(incidents.map((incident) => incident.incident_coordinates));
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (incidents !== null && incidentPlotData != null && incidentMarkers != null) set_is_loading(false);
            }
        }
        fetchDetails();
    }, [incidentUuids, incidents, incidentMarkers]);

    return (
    <div id="incident_container">
       {is_loading ? (
            <div id="loading">Loading...<span className="rotating-arrows">&#8635;</span></div>
       ) : (
       <>
            <div id="top_btn_panel">
                <button type="button" id="download_pdf_btn" disabled> Download PDF </button>
                <button style={{display:'none'}} type="button" onClick={() => call_generate_pdf('word')}> Download Word </button>
                <button style={{display:'none'}} type="button" onClick={() => call_generate_pdf('excel')}> Download excel </button>
                <div className="top_menu_item">
                    <button type="button" className="button_style" onClick={reloadMap}>Reload Report</button>
                </div>
            </div>
            <h1 id="report_title">{ incidentTitle ?? 'UNKNOWN'}</h1>
            <div id="incidentList_intro" className="report_row multi_incident_cont">
                <div className="left">
                    <div className="inner_padding">
                        <p className="sub_title">Filters</p>
                        <table id="header_table">
                        <tbody>
                            <tr>
                                <td className="title">DATE FROM</td> <td>{ incidentsParameters?.multi_incident_date_from.replace("T", " ")?.trim() || 'UNKNOWN'}</td>
                                <td className="title">DATE TO</td> <td>{ incidentsParameters?.multi_incident_date_to.replace("T", " ")?.trim() || 'UNKNOWN'}</td>
                            </tr>
                            <tr>
                                <td className="title">Area</td> <td>{ areaText }</td>
                                <td className="title">Incident Type</td> <td>{ incidentsParameters?.multi_incident_incident_types?.trim() ||'No filter'}</td>
                            </tr>
                            <tr>
                                <td className="title">Time of Day</td> <td>{ incidentsParameters?.multi_incident_time_of_day?.trim() || 'No filter'}</td>
                                <td className="title">Vessel Status</td> <td>{ incidentsParameters?.multi_incident_vessel_status?.trim() || 'No filter'}</td>
                            </tr>
                            <tr>
                                <td className="title">Vessel Type</td> <td>{ incidentsParameters?.multi_incident_vessel_types?.trim() || 'No filter'}</td>
                                <td className="title">Weapons</td> <td>{ incidentsParameters?.multi_incident_weapons?.trim() || 'No filter'}</td>
                            </tr>
                        </tbody>
                        </table>
                    </div>
                    {incidentPlotData != null && incidentPlotData[0]?.plot_name == "All Incidents" ? (<ReportPlot plotData={incidentPlotData[0]} title={incidentPlotData[0]?.plot_name} errorMessage="Incident plot failed" />) : (null)}
                </div>
                <div id="incidents_map_cont" className="right">
                    {incidentMarkers != null ? (<MapComponent key={mapKey} report_data={null} map_ref={map_ref} markers={incidentMarkers}/>) : (null)}
                </div>
            </div>

            {incidentPlotData ? incidentPlotData.map((incidentPlot) =>
                <>
                    {incidentPlot?.plot_name.includes( " Total") ? (
                    <div className="incident_total_cont">
                        <ReportPlot plotData={incidentPlot} title={incidentPlot?.plot_name} errorMessage="Incident plot failed" />
                    </div>
                    ) : incidentPlot?.plot_name != "All Incidents" ? (<ReportPlot plotData={incidentPlot} title={incidentPlot?.plot_name} errorMessage="Incident plot failed" />) : (null)}
                </>
            ) : null}

            <RouteIncidents route_data={null} parsedIncidents={incidents} incidentsListTitle={null} />
       </>
       )}
    </div>
)};
export default IncidentsReport;
//<div className="front_incident_border"></div>